import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosInstance from '../axiosInterceptor';

type UploadStoragePayload = {
  file: File;
  fileType: string;
};

type UploadStorageResult = {
  fileUrl: string;
  response: unknown;
};

type UploadStorageState = {
  loading: boolean;
  data: UploadStorageResult | null;
  error: boolean;
  errorMessage: string;
};

const initialState: UploadStorageState = {
  loading: false,
  data: null,
  error: false,
  errorMessage: '',
};

export const uploadStorageFile = createAsyncThunk<
  UploadStorageResult,
  UploadStoragePayload
>('uploadStorageFile', async (payload) => {
  const { file } = payload;
  if (!file) {
    throw new Error('Missing file for upload');
  }

  const formData = new FormData();
  formData.append('file', file);

  const { data } = await axiosInstance.post('/api/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  const fileUrl = data?.url || data?.file_url || data?.fileUrl;
  if (!fileUrl) {
    throw new Error('Upload failed: missing file URL.');
  }

  return {
    fileUrl,
    response: data,
  };
});

const uploadStorageSlice = createSlice({
  name: 'uploadStorage',
  initialState,
  reducers: {
    resetStorageUpload: (state) => {
      state.loading = false;
      state.data = null;
      state.error = false;
      state.errorMessage = '';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(uploadStorageFile.pending, (state) => {
      state.loading = true;
      state.error = false;
      state.errorMessage = '';
    });
    builder.addCase(uploadStorageFile.fulfilled, (state, action) => {
      state.loading = false;
      state.data = action.payload;
    });
    builder.addCase(uploadStorageFile.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
      state.errorMessage = action.error.message ?? 'Upload failed.';
    });
  },
});

export const { resetStorageUpload } = uploadStorageSlice.actions;
export default uploadStorageSlice.reducer;
